import Swal from "sweetalert2";
import { append_active_organization_form_data, append_active_organization_id } from "@/utils/organization_profile";
import { get_marketplace_display_description } from "@/views/Marketplace/marketplace_helpers";

export const my_shop_product_methods = {
    create_product(should_publish = false) {
        if (this.validate_item_form()) {
            return;
        }

        if (should_publish && !this.has_product_publish_image()) {
            this.handle_missing_publish_image();
            return;
        }

        const product_type = this.product_types.find(
            (type) => String(type.name || "").toLowerCase() === this.modalType.toLowerCase()
        );

        if (!product_type?.id) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Could not determine product type.",
                position: "bottom-end",
                toast: true,
                showConfirmButton: false,
                timer: 2500,
            });
            return;
        }

        this.isLoading = true;
        this.create_action_loading = should_publish ? "publish" : "draft";
        let publish_pending = false;
        const formData = new FormData();
        formData.append("name", this.item_form.name);
        formData.append("price", this.item_form.price);
        formData.append("description", get_marketplace_display_description(this.item_form.description));
        formData.append("base_profile", this.base_profile_id);
        formData.append("product_type_id", product_type.id);
        formData.append("category_id", this.selected_subcategory || "");
        formData.append("attributes", JSON.stringify(this.item_form.attributes || {}));
        formData.append("is_published", should_publish ? "true" : "false");
        append_active_organization_form_data(formData);

        if (this.item_form.service_type_id) {
            formData.append("service_type_id", this.item_form.service_type_id);
        }
        if (this.item_form.service_location) {
            formData.append("location", this.item_form.service_location);
        }
        this.upload_files.forEach((file) => {
            formData.append("media_files", file);
        });

        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/create_product/`,
            method: "POST",
            headers: {
                Authorization: `Token ${this.getToken()}`,
            },
            data: formData,
            processData: false,
            contentType: false,
            success: (response) => {
                if (response.status === "success") {
                    publish_pending = should_publish && !response.data?.is_published;
                    this.handle_created_product(response.data, should_publish);
                    return;
                }

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: response.message || "Failed to create item.",
                    position: "bottom-end",
                    toast: true,
                    showConfirmButton: false,
                    timer: 2500,
                });
            },
            error: (xhr) => {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: xhr.responseText || "Upload failed.",
                    position: "bottom-end",
                    toast: true,
                    showConfirmButton: false,
                    timer: 2500,
                });
            },
            complete: () => {
                if (!publish_pending) {
                    this.isLoading = false;
                    this.create_action_loading = null;
                }
            }
        });
    },
    handle_created_product(new_product, should_publish = false) {
        this.sync_catalogue_items([...this.profile_products, new_product]);

        if (should_publish && !new_product?.is_published) {
            this.publish_created_product(new_product);
            return;
        }

        this.finish_create_product(new_product?.is_published);
    },
    publish_created_product(product) {
        const base_profile_id = product?.base_profile_id || product?.base_profile?.id || this.base_profile_id;

        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/publish_product/`,
            method: "POST",
            headers: {
                Authorization: `Token ${this.getToken()}`,
                "Content-Type": "application/json"
            },
            data: JSON.stringify(append_active_organization_id({
                product_id: product.id,
                base_profile_id
            })),
            success: (response) => {
                if (response.status === "success") {
                    product.is_published = response.is_published;
                    this.sync_catalogue_items([...this.profile_products]);
                    this.finish_create_product(response.is_published);
                    return;
                }

                this.finish_create_product(false, response.message || "Product saved as draft, but publishing failed.");
            },
            error: (xhr) => {
                this.finish_create_product(false, xhr.responseJSON?.message || "Product saved as draft, but publishing failed.");
            },
            complete: () => {
                this.isLoading = false;
                this.create_action_loading = null;
            }
        });
    },
    has_product_publish_image() {
        return (this.upload_files || []).some((file) => file?.type?.startsWith("image/"))
            || (this.previews || []).some((preview) => preview?.type === "image" && !preview?._delete);
    },
    product_has_publish_image(product = {}) {
        return (product?.images || []).some((image) => image?.image_url || image?.url || image?.file_url);
    },
    handle_missing_publish_image() {
        this.errors = {
            ...this.errors,
            product_images: "Add at least one image before publishing, or save this product as draft.",
        };
        this.scroll_to_product_images();
    },
    handle_missing_existing_product_image(product) {
        this.errors = {
            ...this.errors,
            product_images: "Add at least one image before publishing this product.",
        };
        this.open_manage_product_images(product);
    },
    scroll_to_product_images() {
        this.$nextTick(() => {
            const image_section = document.getElementById("add-product-images-section");
            if (!image_section) return;

            image_section.scrollIntoView({ behavior: "smooth", block: "center" });
        });
    },
    scroll_to_manage_product_images() {
        this.$nextTick(() => {
            const image_section = document.getElementById("manage-product-images-section");
            if (!image_section) return;

            image_section.scrollIntoView({ behavior: "smooth", block: "center" });
        });
    },
    finish_create_product(is_published = false, warning_text = "") {
        this.reset_product_form();
        $("#addItemModal").modal("hide");
        Swal.fire({
            icon: warning_text ? "warning" : "success",
            title: warning_text ? "Saved as draft" : "Success",
            text: warning_text || (is_published ? "Product published successfully!" : "Product saved as draft."),
            showConfirmButton: false,
            timer: warning_text ? 2500 : 1000,
            position: "bottom-end",
            toast: true,
        });
    },
    open_manage_modal(product) {
        this.modal_product = product;
        this.isEditing = false;
        this.previews = [];
        this.upload_files = [];
        this.active_modal_image_index = 0;
        $("#manageProductModal").modal("show");
    },
    open_manage_product_images(product) {
        this.edit_product(product);
        $("#manageProductModal").modal("show");
        $("#manageProductModal").one("shown.bs.modal", () => this.scroll_to_manage_product_images());
    },
    toggle_publish(product) {
        if (!product?.is_published && !this.product_has_publish_image(product)) {
            this.handle_missing_existing_product_image(product);
            return;
        }

        const previous_state = product.is_published;
        product.is_published = !product.is_published;
        const base_profile_id = product?.base_profile_id || product?.base_profile?.id || this.base_profile_id;

        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/publish_product/`,
            method: "POST",
            headers: {
                Authorization: `Token ${this.getToken()}`,
                "Content-Type": "application/json"
            },
            data: JSON.stringify(append_active_organization_id({
                product_id: product.id,
                base_profile_id
            })),
            success: (response) => {
                if (response.status === "success") {
                    const updated_product = {
                        ...product,
                        ...(response.data || {}),
                        is_published: response.is_published,
                    };
                    this.upsert_catalogue_listing_from_realtime?.(updated_product);
                    product.is_published = response.is_published;
                    this.modal_product = updated_product;
                    return;
                }

                product.is_published = previous_state;
                Swal.fire({
                    icon: "error",
                    text: response.message || "Failed to update product status.",
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                    toast: true,
                });
            },
            error: (xhr) => {
                product.is_published = previous_state;
                Swal.fire({
                    icon: "error",
                    text: xhr.responseJSON?.message || "Failed to update product status.",
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                    toast: true,
                });
            }
        });
    },
    edit_product(product) {
        this.isEditing = true;
        this.modal_product = product;
        this.update_item_form = {
            product_id: product.id,
            name: product.name,
            price: product.price,
            description: get_marketplace_display_description(product.description),
            subcategory_id: product.category?.id || "",
            attributes: {},
            service_type_id: product.service_details?.service_type_id || "",
            service_type: product?.service_details?.type_name,
            location: product?.service_details?.location || ""
        };
        this.selected_category = product.category || "";
        this.fetch_categories();

        if (this.selected_category) {
            this.fetch_subcategories(this.selected_category);
        }

        if (product.attributes?.length) {
            product.attributes.forEach((attr) => {
                const key = attr.attribute_type?.name || attr.attribute_type;
                const cleanKey = key.toLowerCase();

                if (!Reflect.get(this.update_item_form.attributes, cleanKey)) {
                    Reflect.set(this.update_item_form.attributes, cleanKey, []);
                }
                Reflect.get(this.update_item_form.attributes, cleanKey).push(attr.value);
            });
        }

        this.upload_files = [];
        this.previews = [];
        if (product.images?.length) {
            product.images.forEach((img) => {
                this.previews.push({
                    type: "image",
                    src: img.image_url,
                    id: img.id,
                    _delete: false,
                });
            });
        }
    },
    update_product() {
        if (!this.update_item_form.product_id) return;

        this.isLoading = true;
        const formData = new FormData();
        formData.append("product_id", this.update_item_form.product_id);
        formData.append("name", this.update_item_form.name);
        formData.append("price", this.update_item_form.price);
        formData.append("description", get_marketplace_display_description(this.update_item_form.description));
        formData.append("base_profile_id", this.base_profile_id);
        formData.append("category_id", this.update_item_form.subcategory_id || "");
        formData.append("service_type_id", this.update_item_form.service_type_id || "");
        formData.append("location", this.update_item_form.location || "");
        formData.append("attributes", JSON.stringify(this.update_item_form.attributes || {}));
        append_active_organization_form_data(formData);

        this.upload_files.forEach((file) => formData.append("media_files", file));
        this.previews
            .filter((preview) => preview._delete && preview.id)
            .map((preview) => preview.id)
            .forEach((id) => formData.append("delete_image_ids", id));

        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/update_product/`,
            method: "POST",
            headers: {
                Authorization: `Token ${this.getToken()}`,
            },
            data: formData,
            processData: false,
            contentType: false,
            success: (response) => {
                if (response.status === "success") {
                    const updated_product = response.data;
                    const next_products = [...this.profile_products];
                    const product_index = next_products.findIndex((product) => product.id === updated_product.id);
                    if (product_index !== -1) {
                        next_products.splice(product_index, 1, updated_product);
                    } else {
                        next_products.push(updated_product);
                    }

                    this.sync_catalogue_items(next_products);

                    this.reset_product_form();
                    $("#manageProductModal").modal("hide");
                    Swal.fire({
                        icon: "success",
                        text: "Product updated successfully!",
                        timer: 2000,
                        showConfirmButton: false,
                        position: "bottom-end",
                        toast: true
                    });
                    return;
                }

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: response.message || "Failed to update product",
                    position: "bottom-end",
                    toast: true,
                    showConfirmButton: false,
                    timer: 2500,
                });
            },
            error: () => {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to update product. Please try again.",
                    position: "bottom-end",
                    toast: true,
                    showConfirmButton: false,
                    timer: 2500,
                });
            },
            complete: () => {
                this.isLoading = false;
            }
        });
    },
    delete_product(product) {
        Swal.fire({
            title: "Are you sure?",
            text: "This will permanently delete the product!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (!result.isConfirmed) return;

            this.is_deleting = true;
            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/delete_product/`,
                method: "POST",
                headers: {
                    Authorization: `Token ${this.getToken()}`,
                    "Content-Type": "application/json"
                },
                data: JSON.stringify(append_active_organization_id({
                    product_id: product.id,
                    base_profile_id: this.base_profile_id
                })),
                success: (response) => {
                    if (response.status === "success") {
                        const deleted_product = response.data;
                        this.sync_catalogue_items(
                            this.profile_products.filter((item) => Number(item.id) !== Number(deleted_product.id))
                        );
                        $("#manageProductModal").modal("hide");
                        Swal.fire({
                            icon: "success",
                            title: "Deleted!",
                            text: "Product has been deleted.",
                            timer: 1200,
                            showConfirmButton: false,
                            position: "bottom-end",
                            toast: true,
                        });
                        return;
                    }

                    Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: response.message || "Failed to delete product",
                        position: "bottom-end",
                        toast: true,
                        showConfirmButton: false,
                        timer: 2500,
                    });
                },
                error: (xhr) => {
                    Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: xhr.responseText || "Failed to delete product",
                        position: "bottom-end",
                        toast: true,
                        showConfirmButton: false,
                        timer: 2500,
                    });
                },
                complete: () => {
                    this.is_deleting = false;
                }
            });
        });
    },
};
